import { useEffect, useState, type FormEvent } from 'react';
import QRCode from 'qrcode';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { useResource } from '../lib/useResource';
import type { AgentRelease, EnrollmentCodeRow, Group } from '../lib/types';
import { PROFILE_LABEL, dateTime } from '../lib/labels';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Select,
  Table,
  errorMessage,
} from '../components/ui';

const PROFILE_HELP: Record<'MONITORING' | 'ASSISTED', string> = {
  MONITORING:
    'Inventário, versão do Android, estado do agente e último contato. Não bloqueia nada no aparelho.',
  ASSISTED:
    'Permite, nas próximas versões do agente, launcher com apps autorizados e filtro de domínios com consentimento. Não impede desinstalação nem acesso às configurações.',
};

function useQr(text: string | null) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!text) return setSrc(null);
    void QRCode.toString(text, { type: 'svg', margin: 1 }).then((svg) =>
      setSrc(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`),
    );
  }, [text]);
  return src;
}

function Countdown({ until, compact }: { until: string; compact?: boolean }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const left = Math.max(0, Math.floor((new Date(until).getTime() - now) / 1000));
  if (left === 0) return <span className="text-red-700">expirado</span>;
  if (compact)
    return (
      <span>
        {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')}
      </span>
    );
  return (
    <span>
      expira em {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')}
    </span>
  );
}

function AgentDownload() {
  const release = useResource(async () => {
    const r = await fetch('/downloads/agent/latest.json', { cache: 'no-store' });
    if (!r.ok) throw new Error('sem versão publicada');
    return (await r.json()) as AgentRelease;
  }, []);
  const url = release.data ? new URL(release.data.url, window.location.origin).toString() : null;
  const qr = useQr(url);

  return (
    <Card title="1. Instale o agente no aparelho">
      {release.error != null && (
        <Alert tone="warning">Nenhuma versão do agente publicada neste ambiente ainda.</Alert>
      )}
      {release.data && url && (
        <div className="flex flex-col gap-4 sm:flex-row">
          {qr && (
            <img
              src={qr}
              alt="QR Code para baixar o agente"
              className="size-36 shrink-0 rounded ring-1 ring-slate-200"
            />
          )}
          <div className="min-w-0 space-y-2 text-sm">
            <p>
              Leia o QR Code com a câmera do aparelho ou abra{' '}
              <a href={url} className="font-medium break-all text-indigo-700 underline">
                {url}
              </a>
              .
            </p>
            <p className="text-slate-600">
              Versão <strong>{release.data.versionName}</strong> · publicada{' '}
              {dateTime.format(new Date(release.data.publishedAt))}
            </p>
            <p className="text-slate-600">
              O Android pedirá para permitir a instalação a partir do navegador (instalação fora da
              Play Store). Isso é esperado.
            </p>
            <details className="text-xs text-slate-500">
              <summary className="cursor-pointer">Verificação de integridade</summary>
              <p className="mt-1">
                SHA-256 do APK: <code className="break-all">{release.data.sha256}</code>
              </p>
              <p className="mt-1">
                Certificado de assinatura (SHA-256):{' '}
                <code className="break-all">{release.data.signerSha256}</code>
              </p>
            </details>
          </div>
        </div>
      )}
    </Card>
  );
}

export function EnrollmentPage() {
  const { tenant } = useSession();
  const groups = useResource(() => api.get<Group[]>('/v1/groups', { tenant: true }), [tenant?.id]);
  const codes = useResource(
    () => api.get<EnrollmentCodeRow[]>('/v1/enrollment-codes', { tenant: true }),
    [tenant?.id],
  );
  const [profile, setProfile] = useState<'MONITORING' | 'ASSISTED'>('MONITORING');
  const [groupId, setGroupId] = useState('');
  const [ttl, setTtl] = useState(30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [created, setCreated] = useState<{
    code: string;
    expiresAt: string;
    requestedProfile: string;
  } | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ code: string; expiresAt: string; requestedProfile: string }>(
        '/v1/enrollment-codes',
        { requestedProfile: profile, ttlMinutes: ttl, ...(groupId ? { groupId } : {}) },
        { tenant: true },
      );
      setCreated(r);
      await codes.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Ativação de dispositivos"
        description="Instale o agente e pareie o aparelho com um código de uso único. O usuário do aparelho vê o nome da empresa e o que será coletado antes de confirmar."
      />
      <div className="grid gap-6 lg:grid-cols-2">
        <AgentDownload />
        <Card title="2. Gere um código de pareamento">
          <form onSubmit={submit} className="space-y-4">
            {error != null && <Alert tone="error">{errorMessage(error)}</Alert>}
            <Select
              label="Perfil solicitado"
              value={profile}
              hint={PROFILE_HELP[profile]}
              onChange={(e) => setProfile(e.target.value as 'MONITORING' | 'ASSISTED')}
            >
              <option value="MONITORING">{PROFILE_LABEL.MONITORING}</option>
              <option value="ASSISTED">{PROFILE_LABEL.ASSISTED}</option>
            </Select>
            <p className="rounded-md bg-slate-50 p-2 text-xs text-slate-600 ring-1 ring-slate-200">
              <strong>Controle Corporativo</strong> não usa código: exige aparelho novo ou
              restaurado, provisionado pelo QR Code do assistente de configuração do Android (Fase
              4).
            </p>
            <Select
              label="Grupo (opcional)"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
            >
              <option value="">Sem grupo</option>
              {groups.data?.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
            <Select
              label="Validade"
              value={String(ttl)}
              onChange={(e) => setTtl(Number(e.target.value))}
            >
              <option value="15">15 minutos</option>
              <option value="30">30 minutos</option>
              <option value="60">60 minutos</option>
            </Select>
            <Button type="submit" busy={busy}>
              Gerar código
            </Button>
          </form>
          {created && (
            <div className="mt-5 rounded-md bg-indigo-50 p-4 text-center ring-1 ring-indigo-200">
              <p className="text-xs text-indigo-900">
                Digite no app EndCopilot · {PROFILE_LABEL[created.requestedProfile as 'MONITORING']}
              </p>
              <p
                className="mt-1 font-mono text-3xl font-semibold tracking-widest text-indigo-950"
                aria-label="Código de pareamento"
              >
                {created.code}
              </p>
              <p className="mt-1 text-xs text-indigo-900">
                Uso único · <Countdown until={created.expiresAt} />
              </p>
            </div>
          )}
        </Card>
        <Card title="Códigos ativos" className="lg:col-span-2">
          <Table
            head={['Perfil', 'Grupo', 'Criado', 'Expira', '']}
            empty={
              !codes.loading &&
              codes.data?.length === 0 && <EmptyState>Nenhum código ativo.</EmptyState>
            }
          >
            {codes.data?.map((c) => (
              <tr key={c.id}>
                <td className="px-4 py-2">{PROFILE_LABEL[c.requestedProfile]}</td>
                <td className="px-4 py-2 text-slate-600">{c.group?.name ?? '—'}</td>
                <td className="px-4 py-2 text-slate-500">
                  {dateTime.format(new Date(c.createdAt))}
                </td>
                <td className="px-4 py-2 text-slate-500">
                  <Countdown until={c.expiresAt} compact />
                </td>
                <td className="px-4 py-2 text-right">
                  <Button
                    variant="ghost"
                    className="px-2 py-1 text-xs"
                    onClick={async () => {
                      await api
                        .post(`/v1/enrollment-codes/${c.id}/revoke`, {}, { tenant: true })
                        .catch(() => undefined);
                      await codes.reload();
                    }}
                  >
                    Revogar
                  </Button>
                </td>
              </tr>
            ))}
          </Table>
        </Card>
      </div>
    </>
  );
}
